import { $parse, Parse } from "@opendash/plugin-parse";
import { makeAutoObservable } from "@opendash/state";
import {
  Article,
  Category,
  Document,
  DocumentPage,
  Video,
} from "../types-generated";

export class AppState {
  private allCategories: Category[] = [];

  private languageId: string | null = null;
  private categoryId: string | null = null;
  private articleId: string | null = null;
  private documentId: string | null = null;
  private documentPageId: string | null = null;
  private videoId: string | null = null;

  public loading: boolean = true;

  public category: Category | null = null;

  public articles: Article[] = [];
  public videos: Video[] = [];
  public documents: Document[] = [];
  public documentPages: DocumentPage[] = [];
  public documentPageIndex: number = 0;

  private forceRenderCount: number = 0;

  constructor() {
    makeAutoObservable(this);

    this.fetchAllCategories();

    // @ts-ignore
    window.knowledge = this;
  }

  public get languages(): string[] {
    const result: string[] = this.allCategories
      .map((cat) => cat.language.id)
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort();

    return result;
  }

  public get language(): string | undefined {
    if (!this.languages.includes(this.languageId)) {
      return undefined;
    }

    return this.languageId;
  }

  public get rootCategories() {
    return this.allCategories.filter(
      (cat) => cat.language.id === this.languageId && !cat.parent?.id
    );
  }

  public get childCategories() {
    return this.allCategories.filter(
      (cat) => cat.parent && this.category && cat.parent.id === this.category.id
    );
  }

  public get content(): (string | Category | Article | Document | Video)[] {
    if (!this.language) {
      return this.languages;
    }

    if (!this.category) {
      return this.rootCategories;
    }

    return [
      ...this.childCategories,
      ...this.articles,
      ...this.videos,
      ...this.documents,
    ];
  }

  public get article(): Article | null {
    const article = this.articles.find((a) => a.id === this.articleId);
    return article || null;
  }

  public get video(): Video | null {
    const video = this.videos.find((v) => v.id === this.videoId);
    return video || null;
  }

  public get document(): Document | null {
    const document = this.documents.find((d) => d.id === this.documentId);

    this.handleDocumentChange(document);

    return document || null;
  }

  public setParams(params: Record<string, string>): void {
    if (this.languageId !== params.languageId) {
      this.languageId = params.languageId;
    }

    if (this.categoryId !== params.categoryId) {
      this.categoryId = params.categoryId;

      this.handleCategoryChange();
    }

    if (this.articleId !== params.articleId) {
      this.articleId = params.articleId;
    }

    if (this.documentId !== params.documentId) {
      this.documentId = params.documentId;
    }

    if (this.documentPageId !== params.documentPageId) {
      this.documentPageId = params.documentPageId;
      this.documentPageIndex = (parseInt(params.documentPageId) || 0) - 1;
    }

    if (this.videoId !== params.videoId) {
      this.videoId = params.videoId;
    }
  }

  private async fetchAllCategories() {
    this.setAllCategories(
      await new Parse.Query(Category).limit(9999999).find()
    );
  }

  private setAllCategories(categories: Category[]) {
    this.allCategories = categories;
    this.loading = false;

    this.handleCategoryChange();
  }

  private async handleCategoryChange() {
    const category = this.allCategories.find(
      (category) => category.id === this.categoryId
    );

    this.category = category || null;

    if (!this.category) {
      return this.setContent([], [], []);
    } else {
      this.setContent([], [], []);
    }

    this.setContent(
      await new Parse.Query(Article)
        .equalTo("category", this.category)
        .ascending("label")
        .limit(9999999)
        .find(),

      await new Parse.Query(Video)
        .equalTo("category", this.category)
        .ascending("label")
        .limit(9999999)
        .find(),

      await new Parse.Query(Document)
        .equalTo("category", this.category)
        .ascending("label")
        .limit(9999999)
        .find()
    );
  }

  private setContent(
    articles: Article[],
    videos: Video[],
    documents: Document[]
  ) {
    this.articles = articles;
    this.videos = videos;
    this.documents = documents;
  }

  public async updateContent(obj: Category | Article | Video | Document) {
    const result = await $parse.ui.updateObject(obj.className, obj.id);

    if (!result) {
      return;
    }

    await obj.fetch();

    this.setUpdatedContent(obj);
  }

  public async deleteContent(obj: Category | Article | Video | Document) {
    const result = await $parse.ui.deleteObject(obj.className, obj.id);

    if (!result) {
      return false;
    }

    this.setUpdatedContent(obj, true);

    return true;
  }

  private setUpdatedContent(
    obj: Category | Article | Video | Document,
    deleteObject: boolean = false
  ) {
    if (obj instanceof Category) {
      const category = obj;

      const index = this.allCategories.findIndex((o) => o.id === obj.id);

      if (deleteObject) {
        if (index >= 0) {
          this.allCategories.splice(index, 1);
        }
      } else {
        if (index >= 0) {
          this.allCategories[index] = obj;
        } else {
          this.allCategories.push(obj);
        }
      }
    }

    if (obj instanceof Article) {
      const article = obj;

      const index = this.articles.findIndex((o) => o.id === obj.id);

      if (deleteObject) {
        if (index >= 0) {
          this.articles.splice(index, 1);
        }
      } else {
        if (index >= 0) {
          this.articles[index] = obj;
        } else {
          this.articles.push(obj);
        }
      }
    }

    if (obj instanceof Video) {
      const video = obj;

      const index = this.videos.findIndex((o) => o.id === obj.id);

      if (deleteObject) {
        if (index >= 0) {
          this.videos.splice(index, 1);
        }
      } else {
        if (index >= 0) {
          this.videos[index] = obj;
        } else {
          this.videos.push(obj);
        }
      }
    }

    if (obj instanceof Document) {
      const document = obj;

      const index = this.documents.findIndex((o) => o.id === obj.id);

      if (deleteObject) {
        if (index >= 0) {
          this.documents.splice(index, 1);
        }
      } else {
        if (index >= 0) {
          this.documents[index] = obj;
        } else {
          this.documents.push(obj);
        }
      }
    }

    this.forceRender();
  }

  private async handleDocumentChange(document: Document | undefined) {
    console.log("handleDocChange", document);
    if (!document) {
      return this.setDocumentPages([]);
    }

    this.setDocumentPages(
      await new Parse.Query(DocumentPage)
        .equalTo("document", document)
        .ascending("page")
        .limit(9999999)
        .find()
    );
  }

  private setDocumentPages(pages: DocumentPage[]) {
    this.documentPages = pages;
  }

  public useForceRender() {
    this.forceRenderCount;
  }

  public forceRender() {
    this.forceRenderCount += 1;
  }
}
